import { Button, Icon, Modal, TextAreaInput } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';
import { StudentRiskEvaluationSessionParticipant } from '../types';

type Participant = {
  name: string;
  email: string;
};

export type StudentRiskEvaluationSessionsParticipantModalMethods = {
  open: (name: string, rows: StudentRiskEvaluationSessionParticipant[]) => void;
  close: () => void;
};

type StudentRiskEvaluationSessionsParticipantModalProps = {
  onClose?: () => void;
};

const StudentRiskEvaluationSessionsParticipantModal = (
  { onClose }: StudentRiskEvaluationSessionsParticipantModalProps,
  ref: Ref<StudentRiskEvaluationSessionsParticipantModalMethods>,
) => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_RISK_EVALUATION_SESSIONS_LIST,
  );

  const [sessionName, setSessionName] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);

  const participantsText = useMemo(() => {
    return participants
      .map(
        (participant) =>
          `${t('participantsModal.name', {
            name: participant.name,
          })}\n${t('participantsModal.email', { email: participant.email })}`,
      )
      .join('\n\n');
  }, [participants, t]);

  const handleOpen = useCallback(
    (name: string, rows: StudentRiskEvaluationSessionParticipant[]) => {
      setSessionName(name);
      setParticipants(
        rows.map((row) => ({
          name: row?.account
            ? `${row.account.name} ${row.account.lastNames}`
            : row.name,
          email: row?.account?.email || row.email,
        })),
      );
    },
    [],
  );

  const handleClose = useCallback(() => {
    setParticipants([]);
    setSessionName('');
    !!onClose && onClose();
  }, [onClose]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal isOpen={!!participants.length} toggle={handleClose}>
      <div className="d-flex flex-column align-items-center text-center">
        <Icon name="information" size={51} />
        <h2 className="text-dark fs-22 mt-4 mb-2 w-100 text-center">
          {t('participantsModal.title', { sessionName })}
        </h2>
        <p className="fw-18 w-100 text-center">
          {t('participantsModal.description')}
        </p>
        <div className="w-100 mb-5 mt-4">
          <TextAreaInput
            name="sessionParticipants"
            label={t('participantsModal.label')}
            value={participantsText}
            readOnly
            height={200}
            className="w-100 text-left"
          />
        </div>
        <div className="w-100">
          <Button
            className="w-100"
            type="button"
            size="md"
            fullwidth
            text={t('participantsModal.confirm')}
            onClick={handleClose}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(StudentRiskEvaluationSessionsParticipantModal);
